import React, {
    FC,
    FocusEvent,
    MouseEventHandler,
    memo,
    useCallback,
    useEffect,
    useRef
} from 'react';
import { Box, Image, Text } from '@chakra-ui/react';
import type { IPhoto, IPhotoDimensions, ISPPhotoService } from 'ts/client/types';
import { normalizeBackgroundColor } from 'ts/common/components/slideshows/slideshowUtils';
import type { IUsePhotoActionsProps } from 'ts/common/hooks/usePhotoActions';
import ClientSlideshowWatermarkOverlay from './ClientSlideshowWatermarkOverlay';
import PhotoOverlay from './PhotoOverlay';
import SlideshowGridItemOverlay from './SlideshowGridItemOverlay';
import VideoGridItemOverlay from './VideoGridItemOverlay';
import VideoGridItemPreview from './VideoGridItemPreview';

interface Props extends IUsePhotoActionsProps {
    /** Returns the URL for a given photo and size */
    getPhotoUrl: ISPPhotoService['getUrl'];
    /** Returns the progressive MP4 preview URL for a video */
    getPreviewUrl: ISPPhotoService['getPreviewUrl'];
    /** True only if currently viewing the compare page */
    isComparing: boolean;
    /** True only if the photo is in the cart */
    isInCart: boolean;
    /** True only if the contents of this grid item should be rendered */
    isInView: boolean;
    /** True only if this photo is the one video that should be previewing */
    isPreviewActive: boolean;
    /** True only if two photos have been selected to be compared */
    isReadyToCompare: boolean;
    /** True only if this photo is selected */
    isSelected: boolean;
    /** Called when this photo is clicked */
    onClickPhoto: (photo: IPhoto, goTo?: boolean) => void;
    /** Called when this video has been hovered or focused long enough to start previewing */
    onPreviewEnter: (photoId: IPhoto['id']) => void;
    /** Called when the pointer or focus leaves this video */
    onPreviewLeave: (photoId: IPhoto['id']) => void;
    /** Dimensions for rendering the given photo within the overall grid */
    photoDimensions: IPhotoDimensions;
    /** Angular free digitals service */
    spFreeDigitals: SpAngularJs.ISpFreeDigitals;
    /** Angular translation service */
    translateFilter: SpAngularJs.ITranslateFilter;
}

const handleContextMenu: MouseEventHandler = (event) => event.preventDefault();

/** How long a video must be hovered or focused before it previews, so sweeping the grid fetches nothing */
const PREVIEW_DELAY_MS = 150;

const PhotoGridItem: FC<Props> = ({
    getPhotoUrl,
    getPreviewUrl,
    isBlackAndWhite,
    isComparing,
    isDownloadPossible,
    isFavorite,
    isHidden,
    isInCart,
    isInView,
    isLabeled,
    isPackageBuilderOpen,
    isPreviewActive,
    isReadyToCompare,
    isSelected,
    onClickPhoto,
    onPreviewEnter,
    onPreviewLeave,
    photo,
    photoActions,
    photoDimensions,
    requiresDownloadDestination,
    spFreeDigitals,
    translateFilter,
    $rootScope
}) => {
    const isSlideshowWithNoThumbnail = photo.type === 'slideshow' && !photo.slideshowData?.coverId;
    const slideshowBg = normalizeBackgroundColor(photo.slideshowData?.backgroundColor);
    const slideshowTextColor = slideshowBg === '#ffffff' ? 'black' : 'white';
    const isVideo = photo.type === 'video' && !!photo.videoData;
    const previewUrl = isVideo ? getPreviewUrl(photo) : null;

    const previewTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

    const clearPreviewTimeout = useCallback(() => {
        if (previewTimeoutRef.current) {
            clearTimeout(previewTimeoutRef.current);
            previewTimeoutRef.current = undefined;
        }
    }, []);

    useEffect(() => {
        return clearPreviewTimeout;
    }, [clearPreviewTimeout]);

    const handlePreviewIn = useCallback(() => {
        clearPreviewTimeout();

        previewTimeoutRef.current = setTimeout(() => onPreviewEnter(photo.id), PREVIEW_DELAY_MS);
    }, [clearPreviewTimeout, onPreviewEnter, photo.id]);

    const handlePreviewOut = useCallback(() => {
        clearPreviewTimeout();
        onPreviewLeave(photo.id);
    }, [clearPreviewTimeout, onPreviewLeave, photo.id]);

    /**
     * Keyboard users reach a video through its action buttons rather than the tile itself, so focus
     * previews it just like hovering does. Focus moving between buttons within the same tile is
     * ignored, otherwise every tab stop would restart the video.
     */
    const handleBlur = useCallback(
        (event: FocusEvent<HTMLDivElement>) => {
            if (event.currentTarget.contains(event.relatedTarget as Nullable<Node>)) {
                return;
            }

            handlePreviewOut();
        },
        [handlePreviewOut]
    );

    return (
        <Box
            id={`photoId-${photo.id}`}
            data-testid="photoImage"
            onMouseEnter={isVideo ? handlePreviewIn : undefined}
            onMouseLeave={isVideo ? handlePreviewOut : undefined}
            onFocus={isVideo ? handlePreviewIn : undefined}
            onBlur={isVideo ? handleBlur : undefined}
            cursor="pointer"
            outline="1px solid var(--chakra-colors-gray-100)"
            outlineOffset="-1px"
            position="absolute"
            top={`${photoDimensions.top}px`}
            left={`${photoDimensions.left}px`}
            width={`${photoDimensions.width}px`}
            height={`${photoDimensions.height}px`}
            visibility={isInView ? 'visible' : 'hidden'}
            userSelect="none"
            sx={{
                contentVisibility: isInView ? 'visible' : 'hidden',
                WebkitTouchCallout: 'none',
                WebkitUserSelect: 'none',
                WebkitUserDrag: 'none'
            }}
        >
            {isInView && (
                <>
                    {isSlideshowWithNoThumbnail ? (
                        <Box
                            position="absolute"
                            width="100%"
                            height="100%"
                            bg={slideshowBg}
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                        >
                            <Text
                                fontSize="sm"
                                color={slideshowTextColor}
                                opacity={0.7}
                                textAlign="center"
                                px={4}
                            >
                                {translateFilter('studio.common.slideshowPlayer.photoDeleted')}
                            </Text>
                        </Box>
                    ) : (
                        <Image
                            alt={photo.filename}
                            src={getPhotoUrl(photo, 'l')}
                            onContextMenu={handleContextMenu}
                            filter={photo.filterBlackAndWhite ? 'grayscale(1)' : undefined}
                            position="absolute"
                            width="100%"
                            height="100%"
                            draggable={false}
                            loading="lazy"
                            decoding="async"
                            sx={{
                                pointerEvents: 'none',
                                WebkitTouchCallout: 'none',
                                WebkitUserDrag: 'none',
                                touchAction: 'none'
                            }}
                        />
                    )}
                    {/* Rendered after the thumbnail but before the overlays, so the play icon,
                        duration badge and action icons all keep painting over the preview. */}
                    {isVideo && isPreviewActive && previewUrl && (
                        <VideoGridItemPreview
                            isBlackAndWhite={photo.filterBlackAndWhite}
                            posterUrl={getPhotoUrl(photo, 'l')}
                            previewUrl={previewUrl}
                        />
                    )}
                    {isVideo && photo.videoData && (
                        <VideoGridItemOverlay duration={photo.videoData.duration} />
                    )}
                    {photo.type === 'slideshow' && (
                        <SlideshowGridItemOverlay durationMs={photo.slideshowData?.durationMs} />
                    )}
                    {photo.type === 'slideshow' &&
                        photo.slideshowData?.watermarkId !== null &&
                        photo.slideshowData?.watermarkId !== undefined && (
                            <ClientSlideshowWatermarkOverlay
                                watermarkId={photo.slideshowData.watermarkId}
                                tileWidth={photo.width}
                                tileHeight={photo.height}
                            />
                        )}
                    <PhotoOverlay
                        isBlackAndWhite={isBlackAndWhite}
                        isComparing={isComparing}
                        isDownloadPossible={isDownloadPossible}
                        isFavorite={isFavorite}
                        isHidden={isHidden}
                        isInCart={isInCart}
                        isLabeled={isLabeled}
                        isPackageBuilderOpen={isPackageBuilderOpen}
                        isReadyToCompare={isReadyToCompare}
                        isSelected={isSelected}
                        onClick={onClickPhoto}
                        photo={photo}
                        photoActions={photoActions}
                        requiresDownloadDestination={requiresDownloadDestination}
                        spFreeDigitals={spFreeDigitals}
                        translateFilter={translateFilter}
                        $rootScope={$rootScope}
                    />
                </>
            )}
        </Box>
    );
};

export default memo(PhotoGridItem);
